/**
 * Sessions Report Page
 * Main page component for the Sessions Report feature
 * Matches Flutter's session report screen layout and Figma design
 *
 * HIVE-6245 #11: Column groups persist across page refresh via store
 * HIVE-6245 #12: Always-visible columns remain visible regardless of group toggles
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useSessionsReportStore } from './stores/sessions-report-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { SessionsToolbar } from './components/SessionsToolbar';
import { SessionsGrid } from './components/SessionsGrid';
import { SessionDetailBlade } from './components/SessionDetailBlade';
import { SessionGridRow, SessionFilters } from './types/session-types';
import {
  ColumnGroup,
  getDefaultColumnVisibility,
  getSessionsGridColumns,
} from './components/SessionsGridColumns';

export function SessionsReport() {
  // Translation hook - uses Flutter's translation keys
  const { t } = useTranslation('common');
  const {
    sessions,
    filters,
    isLoading,
    error,
    totalCount,
    currentPage,
    pageSize,
    pageSizeOptions,
    selectedSession,
    showAdvancedFilters,
    enabledColumnGroups,
    isExportingSessions,
    exportProgress,
    tariffLevels,
    setFilters,
    loadSessions,
    loadPage,
    setPageSize,
    setSelectedSession,
    clearSelectedSession,
    toggleAdvancedFilters,
    viewReport,
    setColumnGroupEnabled,
    exportSessions,
    loadTariffLevels,
  } = useSessionsReportStore();

  const { selectedOperator } = useOperatorStore();

  const [isBladeOpen, setIsBladeOpen] = useState(false);

  // Compute column visibility based on enabled groups (from store, persisted)
  // HIVE-6245 #12: Always-visible columns are handled in getDefaultColumnVisibility
  // Pass actual columns with tariff levels and sessions to filter out empty tariff levels
  // Pass VAT Include state for tariff level price calculations
  const columnVisibility = useMemo(
    () => getDefaultColumnVisibility(enabledColumnGroups, getSessionsGridColumns(tariffLevels, sessions, enabledColumnGroups.includes('vatInclude'), t)),
    [enabledColumnGroups, tariffLevels, sessions, t]
  );

  // Load sessions and tariff levels when operator is selected
  // Wait for operator selection before making API calls (X-Operator-Id header required)
  useEffect(() => {
    if (selectedOperator?.operatorId) {
      loadTariffLevels(); // Load tariff names for dynamic column headers
      loadSessions();
    }
  }, [selectedOperator?.operatorId, loadSessions, loadTariffLevels]);

  const handleRowSelect = (session: SessionGridRow) => {
    setSelectedSession(session);
    setIsBladeOpen(true);
  };

  const handleBladeClose = () => {
    setIsBladeOpen(false);
    clearSelectedSession();
  };

  const handleRefresh = useCallback(() => {
    loadSessions();
  }, [loadSessions]);

  const handleViewReport = useCallback(() => {
    viewReport();
  }, [viewReport]);

  const handleFiltersChange = useCallback(
    (newFilters: Partial<SessionFilters>) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const handleColumnGroupChange = useCallback(
    (groupId: ColumnGroup, enabled: boolean) => {
      setColumnGroupEnabled(groupId, enabled);
    },
    [setColumnGroupEnabled]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      loadPage(page);
    },
    [loadPage]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPageSize(size);
    },
    [setPageSize]
  );

  const handleExport = useCallback((exportColumnVisibility: VisibilityState) => {
    // Extract selected columns from visibility state
    const selectedColumns = Object.keys(exportColumnVisibility)
      .filter(col => exportColumnVisibility[col] !== false);

    // Pass selected columns to export function (if none selected, will export all)
    exportSessions(selectedColumns.length > 0 ? selectedColumns : undefined);
  }, [exportSessions]);

  const handleToggleAdvancedFilters = useCallback(() => {
    toggleAdvancedFilters();
  }, [toggleAdvancedFilters]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar with filters and column group toggles */}
      <div className="flex-shrink-0">
        <SessionsToolbar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        enabledColumnGroups={enabledColumnGroups}
        onColumnGroupChange={handleColumnGroupChange}
        totalCount={totalCount}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onViewReport={handleViewReport}
        onExport={handleExport}
        columnVisibility={columnVisibility}
        showAdvancedFilters={showAdvancedFilters}
        onToggleAdvancedFilters={handleToggleAdvancedFilters}
        isExporting={isExportingSessions}
        exportProgress={exportProgress}
        />
      </div>

      {/* Grid - flex-1 to fill remaining space, overflow-hidden to constrain child */}
      <div className="flex-1 min-h-0 overflow-hidden px-6">
        <SessionsGrid
          data={sessions}
          isLoading={isLoading}
          error={error ?? undefined}
          totalCount={totalCount}
          onRowSelect={handleRowSelect}
          onRefresh={handleRefresh}
          selectedSessionId={selectedSession?.sessionId}
          columnVisibility={columnVisibility}
          currentPage={currentPage}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          groupByLocation={filters.groupByLocation}
          tariffLevels={tariffLevels}
          enabledColumnGroups={enabledColumnGroups}
          t={t}
        />
      </div>

      {/* HIVE-6307: Unified Detail Blade with tabs for Details and Meter Values */}
      <SessionDetailBlade
        session={selectedSession}
        isOpen={isBladeOpen}
        onClose={handleBladeClose}
      />
    </div>
  );
}

export default SessionsReport;
